/**
 * Client-side wrapper for the /viewer-api/endo/* endpoints.
 * Mirrors the server shapes defined in src/endoReports.ts so we
 * can serialise spans + ROIs + stills round-trip without impedance.
 */
import { auth } from "@viewer/auth/store";

export type EndoStatus = "draft" | "finalized" | "amended";

export interface EndoLabelWire {
  kind: "anatomy_span" | "lesion_roi" | "captured_still";
  t_start?: number | null;
  t_end?: number | null;
  label_id?: string | null;
  label_name?: string | null;
  label_kind?: "anatomy" | "lesion" | "custom" | null;
  label_color?: string | null;
  roi_shape?: "rect" | "point" | null;
  roi_x?: number | null;
  roi_y?: number | null;
  roi_w?: number | null;
  roi_h?: number | null;
  still_data_url?: string | null;
  still_include?: boolean | null;
}

export interface EndoReportWire {
  id?: string;
  studyUid: string;
  userId: string;
  userName?: string | null;
  status: EndoStatus;
  procedureType: string;
  body: Record<string, string>;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
  labels: EndoLabelWire[];
  seededFrom?: string | null;
}

function authHeaders(): Record<string, string> {
  const a = auth();
  return a?.accessToken ? { Authorization: `Bearer ${a.accessToken}` } : {};
}

export async function fetchReport(studyUid: string): Promise<EndoReportWire | null> {
  const r = await fetch(`/viewer-api/endo/reports/${encodeURIComponent(studyUid)}`, {
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!r.ok) return null;
  return r.json();
}

export async function saveReport(
  studyUid: string,
  payload: { procedureType: string; body: Record<string, string>; labels: EndoLabelWire[] },
): Promise<EndoReportWire> {
  const r = await fetch(`/viewer-api/endo/reports/${encodeURIComponent(studyUid)}`, {
    method: "PUT",
    headers: { "content-type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  if (!r.ok) throw new Error(`save failed ${r.status}`);
  return r.json();
}

export async function finalizeReport(studyUid: string): Promise<EndoReportWire> {
  const r = await fetch(`/viewer-api/endo/reports/${encodeURIComponent(studyUid)}/finalize`, {
    method: "POST",
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!r.ok) throw new Error(`finalize failed ${r.status}`);
  return r.json();
}

export async function amendReport(studyUid: string): Promise<EndoReportWire> {
  const r = await fetch(`/viewer-api/endo/reports/${encodeURIComponent(studyUid)}/amend`, {
    method: "POST",
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!r.ok) throw new Error(`amend failed ${r.status}`);
  return r.json();
}
